import { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { useStoreState } from "ezStore";
import useUserPreferences from "components/hooks/useUserPreferences";
import { UserDashboardWidgetsDocument } from "__generated__/view/dashboard/graphql/dashboard.queries.generated";
import { storedWidgetSchema } from "view/dashboard/utils/dashboard.schema";
import { WidgetTemplate } from "view/dashboard/types/widget";
import { SOURCES } from "view/dashboard/widgets/sources";
import { VISUALS } from "view/dashboard/widgets/visuals";

const PREFERENCE_KEY = "dashboardWidgets";

const parseWidgets = (value: unknown): Array<WidgetTemplate> | undefined => {
	if (!Array.isArray(value)) {
		return undefined;
	}

	return value
		.map(entry => storedWidgetSchema.safeParse(entry))
		.filter(result => result.success)
		.filter(({ data }) => data.source in SOURCES && data.visual in VISUALS)
		.map(({ data }) => ({
			id: data.id,
			source: data.source,
			visual: data.visual,
			config: {
				label: data.config.label,
				url: data.config.url,
				source: data.config.source,
				visual: data.config.visual,
			},
			order: data.order,
		}));
};

export const useWidgets = (defaults: Array<WidgetTemplate>) => {
	const session = useStoreState(store => store.session);
	const saveUserPreferences = useUserPreferences();

	// personal dashboard
	const stored = session.user?.properties.userPreferences?.[PREFERENCE_KEY];
	const own = parseWidgets(stored);

	// organisation dashboard
	const { data } = useQuery(UserDashboardWidgetsDocument, { skip: own !== undefined });
	const organisation = parseWidgets(data?.userDashboardWidgets?.widgets);

	const [ widgets, setWidgets ] = useState<Array<WidgetTemplate>>(own ?? defaults);

	const ownJson = JSON.stringify(stored ?? null);
	const organisationJson = JSON.stringify(organisation ?? null);

	useEffect(() => {
		setWidgets(own ?? organisation ?? []);
	}, [ ownJson, organisationJson ]);

	const persist = (next: Array<WidgetTemplate>) => {
		setWidgets(next);
		void saveUserPreferences({ preferences: { [PREFERENCE_KEY]: next } });
	};

	const save = (template: WidgetTemplate) => {
		persist(widgets.some(widget => widget.id === template.id)
			? widgets.map(widget => widget.id === template.id ? template : widget)
			: [ ...widgets, template ]);
	};

	const reorder = (ordered: Array<WidgetTemplate>) => {
		persist(ordered);
	};

	const remove = (id: string) => {
		persist(widgets.filter(widget => widget.id !== id));
	};

	const reset = () => {
		const preferences = (session.user?.properties.userPreferences ?? {}) as Record<string, unknown>;
		delete preferences[PREFERENCE_KEY];

		void saveUserPreferences({ preferences });
	};

	return {
		widgets,
		save,
		remove,
		reorder,
		reset,
		isOwn: own !== undefined,
	};
};
